/**
 * This module contains all the code that is ran as the JS is parsed and on all pages
 *
 * @module global
 */

import loadPageModules from './modules/load-page-modules/load-page-modules';
import lazySizes from 'lazysizes';

//import 'lazysizes/plugins/unveilhooks/ls.unveilhooks';
import 'lazysizes/plugins/respimg/ls.respimg';
import 'lazysizes/plugins/optimumx/ls.optimumx';
import 'lazysizes/plugins/native-loading/ls.native-loading';
import * as slideoutMenu from './modules/slideout-menu/slideout-menu';
import { detectInternetExplorer } from './modules/ie-detect/ie-detect';
import { isFirefox, isIos } from './modules/mobile-detect/mobile-detect';
import { setCookie, deleteCookie, isMobile, isTablet } from '@remarkablenet/rcm-utils';
import { gtag } from "./modules/utility-functions/utility-functions";
import { init as initbloomreach } from "./modules/bloomreach/bloomreach";

import {
  openOffCanvasPanel,
  closeOffCanvasPanel,
  findClosestOffCanvasPanel
} from './modules/off-canvas-panel/off-canvas-panel';

import VybeCmsIntegration from './modules/vybe-integration/vybe-integration';

lazySizes.cfg.hFac = 0.1;
lazySizes.cfg.loadMore = 2;
lazySizes.cfg.loadHidden = false;

import '../css/global.scss';
import '@remarkablenet/panel-updater/dist/index.css';
import { registerServiceWorker } from '@remarkablenet/rcm-sw';
import { enableScroll } from './modules/utility-functions/utility-functions';
import { debounce } from '@remarkablenet/rcm-utils';
import { customSelectGetCurrentSelection } from './modules/custom-select/custom-select';
import { DEFAULTS as searchUi} from './modules/search/search-ui';

import initSearchTracking  from './modules/search/tracking-events';
import type { SearchTrackingConfig } from './modules/search/types/tracking-config';
/**
 * This module contains all the code that is ran as the JS is parsed for all pages
 *
 * Add code to this section that should be ran on all pages and is mission critical
 * Do not add things like carousel instanciation into this file
 * @name global
 * @async
 */

let menuInitiated = false;
let searchInitiated = false;
let searchTrackingInitiated = false;
let searchModuleLoaded = false;
let windowWidth = window.innerWidth;

export default async () => {
  await detectInternetExplorer();

  // Init Page Modules
  setTimeout(() => {
    console.log('Running Global Setup');
    if (isIos() && document.body) {
      document.body.classList.add('is-ios-device');
    }
    // register the service worker
      registerServiceWorker();
      if (document.body.classList.contains('header-glass')) setupGlassHeader();
      initiateUsp();

    window.addEventListener(
      'resize',
      debounce(() => {
        if (window.innerWidth === windowWidth) return;

        const html = document.documentElement;
        const body = document.body;

        // If any major UI panel is open, keep it open and
        // preserve the scroll-lock / overlay state across resizes.
        const panelOpen =
          body.classList.contains('menu-open') ||
          html.classList.contains('search-open') ||
          html.classList.contains('slideout-module-active') ||
          html.classList.contains('quick-basket-active') ||
          html.classList.contains('quick-wishlist-active') ||
          html.classList.contains('off-canvas-panel-active');

        windowWidth = window.innerWidth;

        if (panelOpen) {
          // Do not reset or close anything while a panel is open.
          // This means:
          // - The panel stays open
          // - `disableScroll` remains applied
          // - The backdrop / overlay remains active
          return;
        }

        resetMenu();
        checkToInitiateMobileMenu();
        checkToInitiateSearch();
      }, 500)
    );
    initiateMobileMenu();
    initiateSearch();
    initSearchTrackingListeners();
    loadPageModules();
    VybeCmsIntegration();
    splitDropdowns();
    mobFirefoxCheck();
    setupFooterAccordion();

      // Bloomreach functions
      initbloomreach();


    const isPlp = document.querySelector('body').classList.contains('listing');
    const isPdp = document.querySelector('body').classList.contains('productPage');

    if (isPlp || isPdp) {
        endlessGainExperienceTargeting(isPlp, isPdp);
    }


  }, 0);


    //headerNavItemTracking();
};

// --------------------SEARCH--------------------//

function checkToInitiateSearch() {
  if (searchInitiated) return;
  initiateSearch();
}

const focusSearch = () => {
  const searchToggle = document.querySelector<HTMLElement>(searchUi.toggleSearch);
  const searchInput = document.querySelector<HTMLElement>('.vybe-search-input');

  if (searchToggle && searchInput) {
    searchToggle.addEventListener('touchend', (e) => {
      e.preventDefault();

      searchInput.focus();
    });

    searchToggle.addEventListener('click', (e) => {
      e.preventDefault();

      searchInput.focus();
    });
  }
};

function loadSearch() {
  if (!searchModuleLoaded) {
    import('./modules/search/search-ui').then((Search) => {
      searchModuleLoaded = true;
      const searchContainerElm = document.querySelector(Search.DEFAULTS.searchContainer) as HTMLElement;
      Search.init();
      Search.openSearchPanel(searchContainerElm).then(() => {
        (searchContainerElm.querySelector(Search.DEFAULTS.searchInput) as HTMLInputElement).focus();
      });
      Search.getDefaultState();
    });
  }
}

function initiateSearch() {
  const searchToggles = document.querySelectorAll(searchUi.toggleSearch);
  if (searchToggles.length > 0) {
    searchToggles.forEach((searchToggle) => {
      searchToggle.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          loadSearch();
          searchInitiated = true;
        },
        {
          once: true
        }
      );
    });
  }
}

// -------------------- Footer --------------------//

const setupFooterAccordion = (): void => {
    console.log('Setting up footer accordion');

    const groups = document.querySelectorAll<HTMLElement>('.js-footer-accordion');
    if (!groups.length) return;

    groups.forEach(group => {
        if ((group as any)._accordionInit) return;
        (group as any)._accordionInit = true;

        const items = group.querySelectorAll<HTMLElement>('.js-footer-accordion-item');

        const open = (item: HTMLElement) => {
            items.forEach(i => i.classList.remove('active'));
            item.classList.add('active');
        };

        const close = (item: HTMLElement) => {
            item.classList.remove('active');
        };

        items.forEach(item => {
            const header = item.querySelector<HTMLElement>('.js-footer-accordion-item-header');
            const content = item.querySelector<HTMLElement>('.js-footer-accordion-item-content');
            item.classList.remove('active');

            if (!header) return;

            header.addEventListener('click', (e) => {
                e.preventDefault();
                const isActive = item.classList.contains('active');
                if (isActive) {
                    close(item);
                } else {
                    open(item);
                }
            });
        });
    });
};

// -------------------- SEARCH TRACKING LISTENERS --------------------//

const initSearchTrackingListeners = (): void => {
  if (!searchTrackingInitiated) {
    const config: SearchTrackingConfig = {
      typingInterval: 400,
      initDelay: 400,
      options: {
        enableBloomreach: true,
        enableSalesfire: false
      },
      trackingTypes: {
        SALESFIRE: {
          parameters: {
            searchTerm: "sfQuery",
          },
          events: {
            searchTracking: "searchFired",
            searchFormSubmitted: "searchFired",
          }
        }
      }
    };

    initSearchTracking(config);
    searchTrackingInitiated = true;
  }
};

/* USP */
const initiateUsp = (): void => {
    const threshold = 5; // small scroll before hiding

    const handleScroll = (): void => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        document.body.classList.toggle('hide-usp', scrollTop > threshold);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('load', handleScroll);
};

/* Header */
export const showSolidHeader = () => {
    if (document.body.classList.contains('header-glass')) document.body.classList.add('header-is-revealed');
};

export const showGlassHeader = (timeout = 200) => {
    const body = document.body;
    if (!body.classList.contains('header-glass')) return;

    setTimeout(() => {
        const html = document.documentElement;

        // If any major UI panel is open, keep the header in its revealed state.
        const panelActive =
            body.classList.contains('menu-open') ||
            html.classList.contains('search-open') ||
            html.classList.contains('slideout-module-active') ||
            html.classList.contains('off-canvas-panel-active') ||
            html.classList.contains('quick-basket-active') ||
            html.classList.contains('quick-wishlist-active');

        if (panelActive) return;

        body.classList.remove('header-is-sticky', 'header-is-revealed');
    }, timeout);
};

const setupGlassHeader = (): void => {
    const header = document.querySelector<HTMLElement>('#main-header');
    const banner = document.querySelector<HTMLElement>('.new-hp-banner');

    if (!header) return;

    const desktopOffset = 0;
    const mobileOffset = 60;
    const offset = isMobile() ? mobileOffset : desktopOffset;

    const upThreshold = isMobile() ? 40 : 25;
    const downThresholdDefault = isMobile() ? 25 : 10;
    const downThreshold = banner ? downThresholdDefault : 0;

    /* pixels needed to switch direction */
    const damping = 5;

    let lastScrollTop = 0;
    let scrollUpDistance = 0;
    let scrollDownDistance = 0;
    let removeRevealTimeout: number | null = null;
    let lastDirection: 'up' | 'down' | null = null;

    const getScrollTop = (): number => document.documentElement.scrollTop || window.scrollY;

    const getTransitionDuration = (): number => {
        const duration = window.getComputedStyle(header).transitionDuration;
        return duration.includes('ms') ? parseFloat(duration) : parseFloat(duration) * 1000;
    };

    const setHeaderState = (state: 'top' | 'sticky' | 'revealed'): void => {
        const body = document.body;

        if (state === 'top') {
            body.classList.remove('header-is-sticky', 'header-is-revealed');
            body.classList.add('header-glass');
            return;
        }

        if (state === 'sticky') {
            body.classList.add('header-is-sticky');
            body.classList.remove('header-is-revealed');
            return;
        }

        if (state === 'revealed') {
            body.classList.remove('header-is-sticky', 'header-glass');
            body.classList.add('header-is-revealed');
        }
    };

    let isResizing = false;
    let resizeTimeout: number | null = null;

    const isUiBlockingScroll = (): boolean =>
        isResizing ||
        document.documentElement.classList.contains('slideout-module-active') ||
        document.body.classList.contains('menu-open') ||
        document.documentElement.classList.contains('search-open');

    const handleResize = (): void => {
        if (!isResizing) isResizing = true;
        if (resizeTimeout) clearTimeout(resizeTimeout);

        resizeTimeout = window.setTimeout(() => isResizing = false, 200);
    };

    const handleScroll = (): void => {
        const currentScrollTop = getScrollTop();
        let delta = currentScrollTop - lastScrollTop;

        if (isUiBlockingScroll()) {
            lastScrollTop = currentScrollTop;
            return;
        }

        if (currentScrollTop <= offset && !isResizing) {
            scrollUpDistance = 0;
            scrollDownDistance = 0;
            lastDirection = null;

            if (!document.body.classList.contains('menu-open') && !document.documentElement.classList.contains('search-open')) setHeaderState('top');

            lastScrollTop = currentScrollTop;
            return;
        }

        const newDirection: 'up' | 'down' = delta > 0 ? 'down' : 'up';

        if (lastDirection && newDirection !== lastDirection && Math.abs(delta) < damping) delta = 0;

        if (newDirection !== lastDirection) {
            if (newDirection === 'down') scrollUpDistance = 0;
            else scrollDownDistance = 0;
        }

        if (delta > 0) {
            scrollDownDistance += delta;

            if (scrollDownDistance >= downThreshold) {
                setHeaderState('sticky');

                if (removeRevealTimeout) clearTimeout(removeRevealTimeout);

                removeRevealTimeout = window.setTimeout(() => {
                    document.body.classList.remove('header-is-revealed', 'header-glass');
                    removeRevealTimeout = null;
                }, getTransitionDuration()) as unknown as number;

                scrollDownDistance = 0;
            }
        } else if (delta < 0) {
            scrollUpDistance += Math.abs(delta);

            if (removeRevealTimeout) {
                clearTimeout(removeRevealTimeout);
                removeRevealTimeout = null;
            }

            if (scrollUpDistance >= upThreshold) {
                setHeaderState('revealed');
                scrollUpDistance = 0;
            }
        }

        if (Math.abs(delta) >= damping) lastDirection = newDirection;
        lastScrollTop = currentScrollTop;
    };

    const initialScrollTop = getScrollTop();

    if (initialScrollTop > offset + downThreshold) document.body.classList.add('header-is-sticky');

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
};


//--------------------MENU--------------------//

const resetMenu = () => document.dispatchEvent(new Event('rc-close-menu-fn'));

function checkToInitiateMobileMenu() {
  if (menuInitiated) return;
  initiateMobileMenu();
}

function initiateMobileMenu() {

    const { menuOpen, menuClose } = slideoutMenu.default();
    menuInitiated = true;

    const navDrawer = document.querySelector('.nav-drawer') as HTMLElement;

    const desktopLinks = document.querySelectorAll<HTMLElement>('.js-open-menu-panel');

    desktopLinks.forEach(link => {

        link.addEventListener('click', (e) => {

            const wrapper = link.closest('.nav-main-links--has-dropdown') as HTMLElement;
            if (!wrapper) return;

            if (wrapper.classList.contains('nav-main-links--no-dropdown')) {
                return;
            }

            const dropdown = wrapper.querySelector<HTMLElement>('.dropdown-menu.off-canvas-panel');
            if (!dropdown) return;

            e.preventDefault();

            // If this dropdown is already active, keep the secondary panel open
            if (dropdown.classList.contains('is-active')) {
                return;
            }

            // Only one primary item should appear open at a time on desktop
            document.querySelectorAll<HTMLElement>('.nav-main-links--has-dropdown.open').forEach(openWrapper => {
                if (openWrapper !== wrapper) {
                    openWrapper.classList.remove('open');
                }
            });

            const anyActiveDropdown = document.querySelector('.off-canvas-panel.is-active') as HTMLElement | null;

            initAccordion(dropdown);
            openFirstAccordion(dropdown);

            if (!anyActiveDropdown) {
                openOffCanvasPanel(dropdown);
            } else {
                dropdown.classList.add('is-active', 'is-visible');
                anyActiveDropdown.classList.remove('is-active', 'is-visible');
                navDrawer?.classList.add('secondary-open');
            }

            wrapper.classList.add('open');
        });
    });

    const panels = document.querySelectorAll('.off-canvas-panel');
    let secondaryCloseTimer: ReturnType<typeof setTimeout> | null = null;

    panels.forEach((panel) => {

        const observer = new MutationObserver(() => {

            const anyActive = document.querySelector('.off-canvas-panel.is-active');

            if (anyActive) {
                if (secondaryCloseTimer) {
                    clearTimeout(secondaryCloseTimer);
                    secondaryCloseTimer = null;
                }
                navDrawer?.classList.add('secondary-open');
            } else {
                secondaryCloseTimer = setTimeout(() => {
                    navDrawer?.classList.remove('secondary-open');
                    secondaryCloseTimer = null;
                }, 50);
            }
        });

        observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
    });

    const closeParentButtons = navDrawer.querySelectorAll<HTMLElement>('.js-close-parent-menu-panel');
    closeParentButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();

            const panel = btn.closest('.off-canvas-panel') as HTMLElement | null;
            if (!panel) return;

            closeOffCanvasPanel(panel, false);
        });
    });

    const mobileTriggers = document.querySelectorAll<HTMLElement>('.js-mobile-trigger');

    mobileTriggers.forEach(trigger => {

        trigger.addEventListener('click', (e) => {

            const wrapper = trigger.closest('.nav-main-links--has-dropdown') as HTMLElement;
            if (!wrapper) return;

            const navTitle = trigger.getAttribute('data-nav-title');

            if (navTitle === 'Country Selector') {
                const dropdown = wrapper.querySelector<HTMLElement>('.dropdown-menu.off-canvas-panel');
                if (!dropdown) return;

                e.preventDefault();

                openOffCanvasPanel(dropdown);
                wrapper.classList.add('open');
                initAccordion(dropdown);
                return;
            }

            const body = wrapper.querySelector<HTMLElement>('.mobile-nav-body');
            if (!body) return;

            e.preventDefault();

            const isOpen = wrapper.classList.contains('is-open');

            document.querySelectorAll<HTMLElement>('.nav-main-links--has-dropdown.is-open').forEach(openItem => {
                if (openItem !== wrapper) openItem.classList.remove('is-open');
            });

            wrapper.classList.toggle('is-open', !isOpen);

            if (!isOpen) {
                initAccordion(body);
            }
        });
    });

    document.addEventListener('rc-close-menu-fn', () => {
        document.body.classList.remove('slideout-panel');
        menuClose();

        const filtersDraw = document.querySelector('.filters-draw') as HTMLElement | null;
        const isSearchOpen = document.documentElement.classList.contains('search-open');

        // don't re-enable scroll if search overlay is open
        if (!isSearchOpen && !filtersDraw?.classList.contains('active')) enableScroll();

        document.querySelectorAll('.off-canvas-panel').forEach(panel => panel.classList.remove('is-active', 'is-visible'));

        document.querySelectorAll<HTMLElement>('.nav-main-links--has-dropdown').forEach(wrapper => {
            wrapper.classList.remove('open');
            wrapper.classList.remove('is-open');
        });

        navDrawer?.classList.remove('secondary-open');

        document.querySelectorAll<HTMLElement>('.nav-accordion__header').forEach(header => header.classList.remove('is-open'));
    });

    document.addEventListener('rc-menu-open-fn', () => {
        navDrawer?.querySelectorAll<HTMLElement>('.mobile-nav-body').forEach(body => {
            initAccordion(body);
        });
    });
}

function setOpenAccordion(panel: HTMLElement, openHeader: HTMLElement | null) {
    panel.querySelectorAll<HTMLElement>('.nav-accordion__header').forEach((header) => {
        header.classList.toggle('is-open', header === openHeader);
    });
}

function openFirstAccordion(panel: HTMLElement) {
    if (window.innerWidth < 992) return;

    const firstHeader = panel.querySelector<HTMLElement>('.nav-section .nav-accordion__header');
    const body = firstHeader?.nextElementSibling as HTMLElement;
    if (!body?.classList.contains('nav-accordion__body') || !body.children.length || firstHeader.classList.contains('is-open')) return;

    setOpenAccordion(panel, firstHeader);
}

function toggleAccordion(header: HTMLElement, panel: HTMLElement) {
    const body = header.nextElementSibling as HTMLElement;
    if (!body?.classList.contains('nav-accordion__body')) return;

    setOpenAccordion(panel, header.classList.contains('is-open') ? null : header);
}

function initAccordion(panel: HTMLElement) {

    const accordions = Array.from(panel.querySelectorAll<HTMLElement>('.nav-accordion'));

    accordions.forEach((accordion) => {

        if (accordion.dataset.accordionProcessed === 'true') return;
        accordion.dataset.accordionProcessed = 'true';

        const headers = Array.from(accordion.querySelectorAll<HTMLElement>('.nav-secondary-links__link.nav-accordion__header'));

        headers.forEach((header) => {

            const trigger = header.querySelector<HTMLElement>('.nav-accordion__trigger');
            if (!trigger) return;

            if (trigger.dataset.bound === 'true') return;
            trigger.dataset.bound = 'true';

            let nextNode = header.nextElementSibling as HTMLElement | null;
            const childNodes: HTMLElement[] = [];

            while (nextNode && !nextNode.classList.contains('nav-accordion__header')) {
                childNodes.push(nextNode);
                nextNode = nextNode.nextElementSibling as HTMLElement | null;
            }

            if (childNodes.length === 0) {
                header.classList.remove('nav-accordion__header');
                trigger.classList.add('nav-no-body');
                trigger.classList.remove('nav-accordion__trigger');
                return;
            }

            const body = document.createElement('div');
            body.className = 'nav-accordion__body';
            const inner = document.createElement('div');
            inner.className = 'nav-accordion__body-inner';

            header.insertAdjacentElement('afterend', body);
            childNodes.forEach(node => inner.appendChild(node));
            body.appendChild(inner);

            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleAccordion(header, panel);
            });
        });
    });
}
function splitDropdowns() {
  if (window.innerWidth >= 992) {
    let dropdowns = document.querySelectorAll('.nav-secondary-links') as NodeListOf<HTMLElement>;
    dropdowns.forEach((dropdown) => {
      let hasSubs = (dropdown.querySelectorAll('.nav-secondary-links__link--has-dropdown') as NodeListOf<HTMLElement>).length > 0;
        let hasThirdLevel = (dropdown.querySelectorAll('.nav-secondary-links__link--has-dropdown .nav-secondary-links__link--has-dropdown') as NodeListOf<HTMLElement>).length > 0;

      if (hasThirdLevel) {
        dropdown.classList.add('nav-secondary-links--has-third-level');
        return;
      }

      if (hasSubs) dropdown.classList.add('nav-secondary-links--has-sub');
    });
  }
}

/**
 * Tracks the clicks on the header topbar navigation items with the class **js-header-top-bar-item-link**.
 *
 * GA Event Name: **header_nav_item_clicked**
 * @returns {void}
 */
/*const headerNavItemTracking = (): void => {
  document.querySelectorAll<HTMLLinkElement>('.js-header-top-bar-item-link').forEach(item => {
    item.addEventListener('click', () => {
      gtag('event', 'header_nav_item_clicked', {
        'target_url': item.getAttribute('href'),
        'current_url': window.location.pathname
      });
    });
  });
};*/

// firefox form validation workaround
const mobFirefoxCheck = () => {
  if (isFirefox() && isMobile()) {
    document.querySelector('body').classList.add('mob-firefox');
  }
};

const endlessGainExperienceTargeting = (isPlp, isPdp) => {
    const category = document.querySelectorAll('li.breadcrumb-item')[1]?.getAttribute('data-breadcrumb');
    const hasMultipleColours = !!document.querySelector('.pdp-multi-buygrid .color-swatches .color-swatch');
    const hasAnySizeOutOfStock = !!document.querySelector('.oos-message');
    let title = '';

    if (isPdp) {
        const titleElement = document.querySelector('.product-info__title');
        title = titleElement ? titleElement.innerHTML.trim() : '';
    } else if (isPlp) {
        const titleElement = document.querySelector('.category-header h1');
        title = titleElement ? titleElement.innerHTML.trim() : '';
    }

    window.egPageTargeting = {
        isPlp,
        isPdp,
        category,
        hasMultipleColours,
        hasAnySizeOutOfStock,
        title
    };

    window.dataLayer.push({
        event: 'EGPageTargeting',
        isPlp,
        isPdp,
        category,
        hasMultipleColours,
        hasAnySizeOutOfStock,
        title
    });
}
